/**
 * Marketing Terminal — Audience Manager
 *
 * Phase 4 minimal: persona list with lifecycle state + transition buttons.
 * Persona research workspace + allocation panel are follow-up enhancements.
 *
 * Master spec §15. Lifecycle: draft → in_research → ready_for_review →
 * approved → in_production → archived. Cowork only references `in_production`
 * personas when drafting content.
 *
 * Backend:
 *   GET  /api/admin/marketing/segments
 *   GET  /api/admin/marketing/personas
 *   GET  /api/admin/marketing/personas/:id
 *   POST /api/admin/marketing/personas/:id/transition
 *   PUT  /api/admin/marketing/personas/:id  (draft + in_research only)
 */

import React, { useState } from 'react';
import {
  Archive,
  ArrowRight,
  CheckCircle2,
  Compass,
  Edit3,
  Eye,
  FileText,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '../../../utils/adminApi';

interface Segment {
  id: string;
  name: string;
  description: string;
  priority: number;
  persona_count: number;
  production_persona_count: number;
}

interface Persona {
  id: string;
  segment_id: string;
  segment_name?: string;
  name: string;
  motivation_key: string;
  primary_message: string;
  pillar_affinity: string[];
  preferred_channels: string[];
  state: string;
  priority: number;
  content_count: number;
  avg_engagement_rate: number | null;
  conversion_rate: number | null;
}

interface PersonasResponse {
  success: boolean;
  data: Persona[];
  counts_by_state: Record<string, number>;
}

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } });

export function AudiencePage() {
  return (
    <QueryClientProvider client={queryClient}>
      <AudienceInner />
    </QueryClientProvider>
  );
}

const STATE_FLOW: Array<{
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  { key: 'draft',             label: 'Draft',            description: 'Initial draft, freely editable', icon: FileText,    color: 'var(--text-tertiary)' },
  { key: 'in_research',       label: 'In Research',      description: 'Active research workspace',       icon: Compass,     color: 'var(--cedar-stream)' },
  { key: 'ready_for_review',  label: 'Ready for Review', description: 'Awaiting approval',                icon: Send,        color: 'var(--status-warning)' },
  { key: 'approved',          label: 'Approved',         description: 'Approved, not yet in production', icon: CheckCircle2,color: 'var(--cedar-light)' },
  { key: 'in_production',     label: 'In Production',    description: 'Cowork uses for drafting',         icon: Sparkles,    color: 'var(--cedar-primary)' },
  { key: 'archived',          label: 'Archived',         description: 'Retired',                          icon: Archive,     color: 'var(--text-tertiary)' },
];

function AudienceInner() {
  const [stateFilter, setStateFilter] = useState<string>('');
  const [selected, setSelected] = useState<Persona | null>(null);
  const queryClient = useQueryClient();

  const { data: segments } = useQuery<{ data: Segment[] }>({
    queryKey: ['marketing-segments'],
    queryFn: () => adminFetch('/api/admin/marketing/segments'),
  });

  const { data: personas } = useQuery<PersonasResponse>({
    queryKey: ['marketing-personas', stateFilter],
    queryFn: () => {
      const qs = stateFilter ? `?state=${stateFilter}` : '';
      return adminFetch<PersonasResponse>(`/api/admin/marketing/personas${qs}`);
    },
  });

  const transition = useMutation({
    mutationFn: ({ id, target }: { id: string; target: string }) =>
      adminFetch(`/api/admin/marketing/personas/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ target_state: target }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['marketing-personas'] }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h1
          className="text-2xl font-bold"
          style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--cedar-ink)' }}
        >
          Audience
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Personas + segments. Lifecycle: draft → in research → ready for review →
          approved → in production. Only <strong>in_production</strong> personas are used
          by Cowork for drafting.
        </p>
      </div>

      {/* Segment summary */}
      {segments?.data && segments.data.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {segments.data.map(s => (
            <div
              key={s.id}
              className="rounded-lg bg-white p-3"
              style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 2px rgba(0,0,0,0.06)' }}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">{s.name}</h3>
                <span className="text-[10px] text-gray-500">priority {s.priority}</span>
              </div>
              <p className="mt-1 text-xs text-gray-600">{s.description}</p>
              <div className="mt-2 text-xs text-gray-500">
                {s.persona_count} personas · {s.production_persona_count} in production
              </div>
            </div>
          ))}
        </div>
      )}

      {/* State filter chips */}
      <div className="flex flex-wrap gap-1">
        {[...STATE_FLOW.map(s => s.key), ''].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStateFilter(s)}
            className="rounded px-2.5 py-1 text-xs font-medium"
            style={
              stateFilter === s
                ? { backgroundColor: 'var(--cedar-primary)', color: '#fff' }
                : { backgroundColor: 'var(--cedar-limestone)', color: 'var(--text-secondary)' }
            }
          >
            {s ? s.replace(/_/g, ' ') : 'all'}
            {s && personas?.counts_by_state?.[s] !== undefined && (
              <span className="ml-1 text-[10px] opacity-75">{personas.counts_by_state[s]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Two-pane layout */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
        {/* List */}
        <div className="space-y-2 lg:col-span-5">
          {personas?.data?.map(p => (
            <PersonaRow
              key={p.id}
              persona={p}
              isSelected={selected?.id === p.id}
              onClick={() => setSelected(p)}
            />
          ))}
          {personas?.data?.length === 0 && (
            <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-400" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
              <Users className="mx-auto mb-2 h-8 w-8 text-gray-300" />
              No personas in this state.
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7">
          {selected ? (
            <PersonaDetail
              persona={selected}
              onTransition={target => transition.mutate({ id: selected.id, target })}
              isTransitioning={transition.isPending}
            />
          ) : (
            <div
              className="rounded-lg bg-white p-12 text-center text-sm text-gray-400"
              style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}
            >
              <Eye className="mx-auto mb-2 h-10 w-10 text-gray-300" />
              Select a persona to view details + transition state
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonaRow({
  persona,
  isSelected,
  onClick,
}: {
  persona: Persona;
  isSelected: boolean;
  onClick: () => void;
}) {
  const stateInfo = STATE_FLOW.find(s => s.key === persona.state);
  const StateIcon = stateInfo?.icon ?? FileText;

  return (
    <button
      onClick={onClick}
      className={`block w-full rounded-lg border-2 bg-white p-3 text-left transition-colors hover:border-gray-200 ${
        isSelected ? '' : 'border-transparent'
      }`}
      style={{
        boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
        borderColor: isSelected ? stateInfo?.color : 'transparent',
      }}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">{persona.name}</span>
            <span className="text-[10px] text-gray-400">priority {persona.priority}</span>
          </div>
          {persona.segment_name && (
            <div className="mt-0.5 text-[10px] text-gray-500">{persona.segment_name}</div>
          )}
          <div className="mt-1 flex flex-wrap gap-1">
            {persona.pillar_affinity?.slice(0, 3).map(p => (
              <span
                key={p}
                className="rounded-full px-1.5 py-0.5 text-[9px] font-medium"
                style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium"
          style={{ backgroundColor: stateInfo?.color + '20' || 'var(--cedar-limestone)', color: stateInfo?.color ?? 'var(--text-secondary)' }}
        >
          <StateIcon className="h-2.5 w-2.5" />
          {persona.state.replace(/_/g, ' ')}
        </span>
      </div>
    </button>
  );
}

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  draft: ['in_research', 'ready_for_review', 'archived'],
  in_research: ['draft', 'ready_for_review', 'archived'],
  ready_for_review: ['draft', 'in_research', 'approved', 'archived'],
  approved: ['in_production', 'draft', 'archived'],
  in_production: ['draft', 'archived'],
  archived: ['draft'],
};

function PersonaDetail({
  persona,
  onTransition,
  isTransitioning,
}: {
  persona: Persona;
  onTransition: (target: string) => void;
  isTransitioning: boolean;
}) {
  const allowed = ALLOWED_TRANSITIONS[persona.state] ?? [];
  const stateInfo = STATE_FLOW.find(s => s.key === persona.state);

  return (
    <div className="space-y-4 rounded-lg bg-white p-4" style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.1)' }}>
      <div>
        <div className="text-xs font-medium uppercase tracking-wider" style={{ color: stateInfo?.color }}>
          {persona.state.replace(/_/g, ' ')} · {stateInfo?.description}
        </div>
        <h2 className="mt-1 text-xl font-bold text-gray-900">{persona.name}</h2>
        <div className="mt-1 text-xs text-gray-500">{persona.segment_name}</div>
      </div>

      {/* Lifecycle progress bar */}
      <div className="flex items-center gap-1">
        {STATE_FLOW.filter(s => s.key !== 'archived').map((s, i) => {
          const Icon = s.icon;
          const idx = STATE_FLOW.findIndex(x => x.key === persona.state);
          const stateIdx = STATE_FLOW.findIndex(x => x.key === s.key);
          const reached = stateIdx <= idx && persona.state !== 'archived';
          return (
            <React.Fragment key={s.key}>
              <div
                className="flex flex-col items-center gap-0.5"
                title={s.description}
              >
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-full"
                  style={{ backgroundColor: reached ? s.color : 'var(--border)' }}
                >
                  <Icon className="h-3 w-3 text-white" />
                </div>
                <span
                  className="text-[9px] font-medium"
                  style={{ color: reached ? s.color : 'var(--text-tertiary)' }}
                >
                  {s.label}
                </span>
              </div>
              {i < STATE_FLOW.length - 2 && (
                <ArrowRight className="h-3 w-3 text-gray-300" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Persona content */}
      {persona.primary_message && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Primary message</div>
          <blockquote className="mt-1 border-l-4 pl-3 text-sm italic text-gray-800" style={{ borderColor: 'var(--cedar-primary)' }}>
            "{persona.primary_message}"
          </blockquote>
        </div>
      )}

      {persona.pillar_affinity?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Pillar affinity</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {persona.pillar_affinity.map(p => (
              <span
                key={p}
                className="rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-primary)' }}
              >
                {p}
              </span>
            ))}
          </div>
        </div>
      )}

      {persona.preferred_channels?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Preferred channels</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {persona.preferred_channels.map(c => (
              <span key={c} className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Performance */}
      {(persona.content_count > 0 || persona.avg_engagement_rate || persona.conversion_rate) && (
        <div className="grid grid-cols-3 gap-2 rounded bg-gray-50 p-2 text-xs">
          <div>
            <div className="text-[10px] uppercase text-gray-500">Content</div>
            <div className="font-bold text-gray-900">{persona.content_count}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500">Avg eng.</div>
            <div className="font-bold text-gray-900">
              {persona.avg_engagement_rate ? `${(persona.avg_engagement_rate * 100).toFixed(1)}%` : '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-gray-500">Conv. rate</div>
            <div className="font-bold text-gray-900">
              {persona.conversion_rate ? `${(persona.conversion_rate * 100).toFixed(2)}%` : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Transition buttons */}
      {allowed.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-3">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Transition to:</span>
          {allowed.map(target => {
            const tInfo = STATE_FLOW.find(s => s.key === target);
            const Icon = tInfo?.icon ?? Edit3;
            return (
              <button
                key={target}
                onClick={() => onTransition(target)}
                disabled={isTransitioning}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: tInfo?.color ?? 'var(--text-secondary)' }}
              >
                <Icon className="h-3 w-3" />
                {target.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AudiencePage;
